import MainLayout from "@/components/layouts";
import {
    Card,
    CardHeader,
    CardTitle,
    CardContent,
} from "@/components/ui/card";
import {Checkbox} from "@/components/ui/checkbox";
import {Switch} from "@/components/ui/switch";
import {Label} from "@/components/ui/label";
import {Button} from "@/components/ui/button";
import {Skeleton} from "@/components/ui/skeleton";
import {useNotificationSettings} from "./notification.hooks";

export default function NotificationPage() {
    const {
        settings,
        loading,
        saving,
        error,
        retryFetch,
        onFormSubmit,
        email,
        setEmail,
        sms,
        setSms,
        groupChat,
        setGroupChat,
    } = useNotificationSettings();

    const breadcrumb = [
        {name: "Settings", href: "#"},
        {name: "Notifications", href: "#"},
    ];

    const emailRows = [
        {
            key: "transaction",
            title: "Work and Billing",
            desc: "Appointment, daily work, invoice, payment, invite, and autopay emails.",
        },
        {
            key: "product",
            title: "Product Updates",
            desc: "Feature announcements and product-related updates from ShyneCore.",
        },
        {
            key: "marketing",
            title: "Marketing",
            desc: "Promotional messages, offers, and newsletters.",
        },
    ] as const;

    const smsRows = [
        {
            key: "transaction",
            title: "Work and Billing",
            desc: "Appointment, daily work, invoice, payment, invite, and autopay text messages.",
        },
        {
            key: "product",
            title: "Product Updates",
            desc: "Product-related text messages from ShyneCore.",
        },
    ] as const;

    if (error && !loading && !settings) {
        return (
            <MainLayout breadcrumb={breadcrumb} pageHeader={{
                title: "Notifications",
                subtitle: "Manage your notification settings",
            }}>
                <Card>
                    <CardHeader>
                        <CardTitle>Unable to load notifications</CardTitle>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-4">
                        <p className="text-sm text-muted-foreground">{error}</p>
                        <Button type="button" className="w-fit" onClick={retryFetch}>
                            Retry
                        </Button>
                    </CardContent>
                </Card>
            </MainLayout>
        );
    }

    if (loading || !settings) {
        return (
            <MainLayout breadcrumb={breadcrumb} pageHeader={{
                title: "Notifications",
                subtitle: "Manage your notification settings",
            }}>
                <Skeleton className="h-40 w-full"/>
            </MainLayout>
        );
    }

    return (
        <MainLayout breadcrumb={breadcrumb} pageHeader={{
            title: "Notifications",
            subtitle: "Manage your notification settings",
        }}>

            <div className="container mx-auto">
                <form onSubmit={onFormSubmit} className="flex flex-col gap-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Email Notifications</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {emailRows.map((row) => (
                                <div
                                    key={row.key}
                                    className="flex items-center justify-between"
                                >
                                    <Label
                                        htmlFor={row.key}
                                        className="flex flex-col gap-2 items-start"
                                    >
                                        <p className="text-sm font-medium">{row.title}</p>
                                        <p className="text-sm text-muted-foreground">
                                            {row.desc}
                                        </p>
                                    </Label>
                                    <Checkbox
                                        id={row.key}
                                        checked={email[row.key]}
                                        onCheckedChange={(checked) =>
                                            setEmail((prev) => ({
                                                ...prev,
                                                [row.key]: checked === true,
                                            }))
                                        }
                                    />
                                </div>
                            ))}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>SMS Notifications</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {smsRows.map((row) => (
                                <div key={row.key} className="flex items-center justify-between">
                                    <Label htmlFor={`sms-${row.key}`} className="flex flex-col gap-2 items-start">
                                        <p className="text-sm font-medium">{row.title}</p>
                                        <p className="text-sm text-muted-foreground">{row.desc}</p>
                                    </Label>
                                    <Switch
                                        id={`sms-${row.key}`}
                                        checked={sms[row.key]}
                                        onCheckedChange={(checked) =>
                                            setSms((prev) => ({
                                                ...prev,
                                                [row.key]: checked,
                                            }))
                                        }
                                    />
                                </div>
                            ))}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Chat Settings</CardTitle>
                        </CardHeader>
                        <CardContent className="flex items-center justify-between">
                            <Label
                                htmlFor="chat"
                                className="flex flex-col gap-2 items-start"
                            >
                                <p className="text-sm font-medium">
                                    Group Chats
                                </p>
                                <p className="text-sm text-muted-foreground">
                                    Include me in appointment and daily work group chats.
                                </p>
                            </Label>
                            <Switch
                                id="chat"
                                checked={groupChat}
                                onCheckedChange={setGroupChat}
                            />
                        </CardContent>
                    </Card>

                    <div className="pt-2">
                        <Button type="submit" disabled={saving}>
                            {saving ? "Saving…" : "Save Changes"}
                        </Button>
                    </div>
                </form>
            </div>
        </MainLayout>
    );
}
